import { useCallback, useMemo } from 'react'

import { useQueryParams } from './useQueryParams'
import { useQueryUpdate } from './useQueryUpdate'

/**
 * Typed URL-state for list pages (filters, search, pagination, sorting).
 *
 * Values default to `default` when the URL omits them or carries an invalid
 * value. Values equal to `default` are stripped from the URL on write, so
 * "clean" defaults stay clean. Changing any non-`page` key auto-resets `page`
 * to 1 (the standard "new search → first page" UX).
 */

type StringSpec = { type: 'string'; default: string }
type BooleanSpec = { type: 'boolean'; default: boolean }
type NumberSpec = {
  type: 'number'
  default: number
  allowed?: readonly number[]
}
type EnumSpec<V extends string> = {
  type: 'enum'
  default: V
  values: readonly V[]
}

export type ListParamSpec =
  | StringSpec
  | BooleanSpec
  | NumberSpec
  | EnumSpec<string>

export type ListSchema = Record<string, ListParamSpec>

type SpecValue<S extends ListParamSpec> =
  S extends EnumSpec<infer V>
    ? V
    : S extends BooleanSpec
      ? boolean
      : S extends NumberSpec
        ? number
        : string

export type ListValues<S extends ListSchema> = {
  [K in keyof S]: SpecValue<S[K]>
}

const PAGE_KEY = 'page'
const TRUE_QUERY_VALUES = new Set(['true'])
const FALSE_QUERY_VALUES = new Set(['false'])

const coerce = (raw: unknown, spec: ListParamSpec): unknown => {
  const value = Array.isArray(raw) ? raw[0] : raw

  if (value == null || value === '') return spec.default

  if (spec.type === 'boolean') {
    if (typeof value === 'boolean') return value

    const normalized = String(value).toLowerCase()
    if (TRUE_QUERY_VALUES.has(normalized)) return true
    if (FALSE_QUERY_VALUES.has(normalized)) return false

    return spec.default
  }

  if (spec.type === 'number') {
    const n = Number(value)
    if (!Number.isFinite(n)) return spec.default
    if (spec.allowed && !spec.allowed.includes(n)) return spec.default
    return n
  }

  if (spec.type === 'enum') {
    return (spec.values as readonly string[]).includes(String(value))
      ? String(value)
      : spec.default
  }

  return String(value)
}

const serializeFor = <S extends ListSchema>(
  schema: S,
  key: keyof S,
  value: unknown
): unknown => {
  const spec = schema[key]

  if (value === spec.default) return undefined
  if (spec.type === 'boolean') return value ? 'true' : 'false'

  return value
}

export const useListQueryState = <const S extends ListSchema>(schema: S) => {
  const raw = useQueryParams()
  const { updateQuery } = useQueryUpdate()

  const values = useMemo(() => {
    const out = {} as ListValues<S>
    for (const key of Object.keys(schema) as Array<keyof S>) {
      out[key] = coerce(raw[key as string], schema[key]) as SpecValue<
        S[typeof key]
      >
    }
    return out
  }, [raw, schema])

  const setMany = useCallback(
    (patch: Partial<ListValues<S>>, options?: { resetPage?: boolean }) => {
      const next: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(raw)) {
        if (!(k in schema)) next[k] = v
      }
      const merged = { ...values, ...patch }
      for (const key of Object.keys(schema) as Array<keyof S>) {
        next[key as string] = serializeFor(schema, key, merged[key])
      }

      const touchedNonPage = Object.keys(patch).some((k) => k !== PAGE_KEY)
      const touchedPage = PAGE_KEY in patch
      const shouldResetPage =
        (options?.resetPage ?? touchedNonPage) &&
        !touchedPage &&
        PAGE_KEY in schema
      if (shouldResetPage) {
        next[PAGE_KEY] = serializeFor(
          schema,
          PAGE_KEY,
          schema[PAGE_KEY].default
        )
      }

      // `useQueryUpdate` only strips empty strings and null, not undefined —
      // we'd end up with bare `?search&page` entries.
      for (const k of Object.keys(next)) {
        if (next[k] === undefined) delete next[k]
      }

      updateQuery(next, true)
    },
    [raw, schema, updateQuery, values]
  )

  const set = useCallback(
    <K extends keyof S>(key: K, value: SpecValue<S[K]>) => {
      setMany({ [key]: value } as unknown as Partial<ListValues<S>>)
    },
    [setMany]
  )

  const reset = useCallback(() => {
    const next: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(raw)) {
      if (!(k in schema)) next[k] = v
    }
    updateQuery(next, true)
  }, [raw, schema, updateQuery])

  const isDirty = useMemo(
    () =>
      (Object.keys(schema) as Array<keyof S>).some(
        (key) => values[key] !== schema[key].default
      ),
    [schema, values]
  )

  return { values, set, setMany, reset, isDirty }
}
